import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Trophy, MapPin, Award, Share2, UserRound, ArrowLeft, Crown } from "lucide-react";
import { toast } from "sonner";
import { api, avatarUrlOf } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { COUNTRIES } from "@/lib/countries";
import { Button } from "@/components/ui/button";
import { AvatarImg } from "@/components/AvatarImg";
import { PageLoader } from "@/components/FindoSpinner";

const BADGE_META = {
  bronze: { label: "hc.bronze", cls: "bg-amber-100 text-amber-700 border-amber-200" },
  silver: { label: "hc.silver", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  gold: { label: "hc.gold", cls: "bg-amber-50 text-amber-600 border-amber-300" },
};

function countryLabel(code) {
  const c = COUNTRIES.find((x) => x.code === (code || "").toUpperCase());
  return c ? `${c.flag} ${c.name}` : code;
}

export default function TrouveurProfile() {
  const { id } = useParams();
  const { t } = useLang();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    api(`/public/finders/${id}`, { auth: false })
      .then((p) => { setProfile(p); setLoading(false); })
      .catch(() => { setNotFound(true); setLoading(false); });
  }, [id]);

  function share() {
    const url = window.location.href;
    const text = t("fp.shareText").replace("{name}", profile?.name || "");
    if (navigator.share) navigator.share({ title: "findloop", text, url }).catch(() => {});
    else navigator.clipboard.writeText(`${text} ${url}`).then(() => toast.success(t("an.linkCopied"))).catch(() => {});
  }

  const badge = profile?.badge ? BADGE_META[profile.badge] : null;

  return (
    <div className="min-h-full bg-findo-cream">
      <main className="max-w-2xl mx-auto w-full px-6 py-12">
        {loading ? (
          <PageLoader label={t("c.loading")} className="py-16" size={32} />
        ) : notFound || !profile ? (
          <div className="text-center text-slate-400 py-20 bg-white rounded-2xl border border-border" data-testid="fp-notfound">
            <UserRound size={40} className="mx-auto text-slate-200" />
            <p className="mt-3 font-semibold">{t("fp.notFound")}</p>
          </div>
        ) : (
          <div className="bg-white rounded-3xl border border-border card-shadow overflow-hidden" data-testid="finder-profile">
            <div className="bg-gradient-to-br from-findo-navy to-[#0f1e3a] px-8 pt-10 pb-8 text-center">
              <div className="h-24 w-24 mx-auto rounded-full overflow-hidden bg-white/10 border-2 border-white/30 grid place-items-center">
                {avatarUrlOf(profile) ? <AvatarImg src={avatarUrlOf(profile)} alt="" className="h-full w-full object-cover" /> : <UserRound size={44} className="text-white/50" />}
              </div>
              <h1 className="mt-4 font-display text-3xl font-semibold text-white" data-testid="fp-name">{profile.name}</h1>
              {badge && (
                <span className={`mt-2 inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full border ${badge.cls}`} data-testid="fp-badge">
                  <Award size={12} /> {t("fp.badge")} {t(badge.label)}
                </span>
              )}
              {profile.hero_finder && (
                <span className="mt-2 ml-2 inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full border bg-amber-400/20 text-amber-200 border-amber-300/40" data-testid="fp-hero-badge">
                  <Trophy size={12} /> {t("fp.hero")}
                </span>
              )}
              {(profile.city || profile.country) && (
                <p className="mt-2 text-sm text-white/70 flex items-center justify-center gap-1">
                  <MapPin size={13} /> {[profile.city, profile.country ? countryLabel(profile.country) : null].filter(Boolean).join(" · ")}
                </p>
              )}
            </div>

            <div className="px-8 pt-6">
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-findo-cream/50 rounded-2xl border border-border p-5 text-center" data-testid="fp-score-year">
                  <p className="font-display text-4xl font-semibold text-findo-red leading-tight">{profile.score}</p>
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mt-1">{t("fp.scoreYear")} {profile.year}</p>
                </div>
                <div className="bg-findo-cream/50 rounded-2xl border border-border p-5 text-center" data-testid="fp-score-total">
                  <p className="font-display text-4xl font-semibold text-findo-navy leading-tight">{profile.total_score}</p>
                  <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mt-1">{t("fp.scoreTotal")}</p>
                </div>
              </div>
            </div>

            <div className="p-8 text-center">
              <p className="text-slate-600 flex items-center justify-center gap-1.5">
                <Trophy size={16} className="text-amber-400" /> {t("fp.tagline")}
              </p>
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Button onClick={share} className="rounded-full h-11 px-6 font-bold bg-findo-navy hover:bg-findo-navy/90" data-testid="fp-share">
                  <Share2 size={15} className="mr-1.5" /> {t("fp.share")}
                </Button>
                <Link to="/mon-compte">
                  <Button variant="outline" className="rounded-full h-11 px-6 font-bold border-findo-navy text-findo-navy hover:bg-findo-navy/5" data-testid="fp-become">
                    <Crown size={15} className="mr-1.5" /> {t("home.champBecome")}
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
